/**
 * Single source for the multisig co-signing string constants, so no screen or
 * hook spells one of them out by hand.
 *
 * The session statuses and the organization's enrollment state mirror the
 * backend enums (`internal/data/multisig_enrollment_session.go` and
 * `internal/data/organizations.go`); the view names are frontend-only and
 * describe what the co-signing card shows.
 */

/** Mirrors `data.MultisigEnrollmentSessionStatus`. */
export const ENROLLMENT_STATUS = {
  PENDING_OWNER1_KEY: "PENDING_OWNER1_KEY",
  PENDING_OWNER2_KEY: "PENDING_OWNER2_KEY",
  READY: "READY",
  SUBMITTING: "SUBMITTING",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
  FAILED: "FAILED",
} as const;

export type MultisigEnrollmentSessionStatus =
  (typeof ENROLLMENT_STATUS)[keyof typeof ENROLLMENT_STATUS];

/**
 * Statuses the backend still moves forward on its own. Anything else is
 * terminal, and the backend stops serving the session (404).
 */
export const ACTIVE_ENROLLMENT_STATUSES: ReadonlyArray<MultisigEnrollmentSessionStatus> = [
  ENROLLMENT_STATUS.PENDING_OWNER1_KEY,
  ENROLLMENT_STATUS.PENDING_OWNER2_KEY,
  ENROLLMENT_STATUS.READY,
  ENROLLMENT_STATUS.SUBMITTING,
];

/** Mirrors `data.MultisigEnrollmentStatus` on the organization. */
export const MULTISIG_ORG_STATUS = {
  BACKEND_ONLY: "BACKEND_ONLY",
  ENROLLED: "ENROLLED",
} as const;

export type MultisigEnrollmentStatus =
  (typeof MULTISIG_ORG_STATUS)[keyof typeof MULTISIG_ORG_STATUS];

/** Mirrors `data.MultisigGovernanceMode`. */
export const MULTISIG_GOVERNANCE_MODE = {
  SINGLE_OWNER: "SINGLE_OWNER",
  DUAL_OWNER: "DUAL_OWNER",
} as const;

export type MultisigGovernanceMode =
  (typeof MULTISIG_GOVERNANCE_MODE)[keyof typeof MULTISIG_GOVERNANCE_MODE];

/** Mirrors `data.MultisigSignerRole`. */
export const MULTISIG_SIGNER_ROLE = {
  OWNER: "OWNER",
  FINANCIAL_CONTROLLER: "FINANCIAL_CONTROLLER",
} as const;

export type MultisigSignerRole = (typeof MULTISIG_SIGNER_ROLE)[keyof typeof MULTISIG_SIGNER_ROLE];

/** Mirrors `data.MultisigSignerStatus`. */
export const MULTISIG_SIGNER_STATUS = {
  NONE: "NONE",
  ACTIVE: "ACTIVE",
  REMOVED: "REMOVED",
} as const;

export type MultisigSignerStatus =
  (typeof MULTISIG_SIGNER_STATUS)[keyof typeof MULTISIG_SIGNER_STATUS];

/** What the co-signing card renders. Frontend-only. */
export const CO_SIGNING_VIEW = {
  INITIALIZE: "INITIALIZE",
  MY_TURN: "MY_TURN",
  PENDING_OTHERS: "PENDING_OTHERS",
  ACTIVE: "ACTIVE",
  EXPIRED: "EXPIRED",
  NOT_APPLICABLE: "NOT_APPLICABLE",
} as const;

export type CoSigningView = (typeof CO_SIGNING_VIEW)[keyof typeof CO_SIGNING_VIEW];

/** Co-signing always starts with two Owners (docs/sdp/multisig.md, Enrollment). */
export const ENROLLMENT_REQUIRED_SIGNERS = 2;
